import React from 'react';
import { navigate } from 'gatsby';
import Button from '@ingka/button';
import chevronLeft from '@ingka/ssr-icon/paths/chevron-left';
import closeIcon from '@ingka/ssr-icon/paths/cross';
import styled from 'styled-components';
import Progress, { getActiveStep, Page } from './Progress';
import { useStateSelector } from '../store';
import {
  addPersistingQueryParamsToPath,
  getPrevPath,
} from '../helpers/urlHelper';
import { EventName, sendEventToParent } from '../eventPublisher';
import { colors } from '../styles';
import useTranslation from '../hooks/useTranslation';
import { selectAppOperation } from '../reducers/selectors';
interface StickyHeaderProps {
  page?: Page;
  children?: React.ReactNode;
  isLoading?: boolean;
  customEventName?: EventName;
}

export const HEADER_HEIGHT = '72px';

const HeaderContainer = styled.div`
  background-color: ${colors.neutral.white};
  padding: 1rem;
  position: sticky;
  top: 0;
  z-index: 300;
  height: ${HEADER_HEIGHT};
`;

const HeaderControllers = styled.div<{ $multiple: boolean }>`
  display: flex;
  justify-content: ${(props) =>
    props.$multiple ? 'space-between' : 'flex-end'};
`;

const StickyHeader: React.FC<StickyHeaderProps> = ({
  page,
  children,
  isLoading,
  customEventName = 'BOKA_CLOSED',
}): JSX.Element => {
  const { t } = useTranslation();
  const operation = useStateSelector(selectAppOperation);
  const disabledPrevButton = useStateSelector(
    (state) => state.app.disabledPrevButton,
  );
  const step = page && getActiveStep(disabledPrevButton, page);

  return (
    <HeaderContainer>
      <HeaderControllers $multiple={!!step && step > 1}>
        {step && step > 1 && (
          <Button
            type="tertiary"
            iconOnly
            onClick={() =>
              navigate(addPersistingQueryParamsToPath(getPrevPath(operation)))
            }
            size="small"
            ssrIcon={chevronLeft}
            disabled={isLoading}
            aria-label={t('components.stickyHeader.navigateBack')}
            data-cy="navigate-back-in-sticky-header"
          />
        )}
        {page && <Progress page={page} />}
        <Button
          type="tertiary"
          iconOnly
          onClick={() => sendEventToParent(customEventName)}
          size="small"
          ssrIcon={closeIcon}
          disabled={isLoading}
          aria-label={t('components.stickyHeader.closeModal')}
          data-cy="exit-button-in-sticky-header"
        />
      </HeaderControllers>
      {children}
    </HeaderContainer>
  );
};

export default StickyHeader;
